import { Dispatch, RefObject, SetStateAction, useEffect } from "react";

import { SelectOption } from "./Select";

export const useChipOverflow = ({
  isMultiSelect,
  chipContainerRef,
  selectContainerRef,
  selectedOptions,
  selectedValues,
  setVisibleChipCount
}: {
  isMultiSelect: boolean;
  chipContainerRef: RefObject<HTMLDivElement>;
  selectContainerRef: RefObject<HTMLDivElement>;
  selectedOptions: SelectOption[];
  selectedValues: string[] | undefined;
  setVisibleChipCount: Dispatch<SetStateAction<number | null>>;
}) => {
  useEffect(() => {
    if (!isMultiSelect || !chipContainerRef.current || !selectContainerRef.current || selectedOptions.length === 0) {
      setVisibleChipCount(null);
      return;
    }

    let rafId: number | null = null;

    const calculateVisibleChips = () => {
      const container = selectContainerRef.current;
      const chipContainer = chipContainerRef.current;

      if (!container || !chipContainer) return;

      // wait for next frame to ensure chips are rendered
      rafId = requestAnimationFrame(() => {
        const containerWidth = container.clientWidth;
        const rightIconSpace = 120; // space for search / clear icons and padding
        const availableWidth = containerWidth - rightIconSpace;

        // get all chip elements (filter out any that are display:none)
        const chips = Array.from(chipContainer.children).filter((child) => child instanceof HTMLElement && child.offsetWidth > 0) as HTMLElement[];

        if (chips.length === 0) return;

        let totalWidth = 0;
        let count = 0;
        const gap = 4; // gap-1 = 4px
        // space for +n chip (approximate)
        const overflowChipWidth = 50;

        for (let i = 0; i < chips.length; i++) {
          const chipWidth = chips[i].offsetWidth;
          const widthWithGap = chipWidth + (i > 0 ? gap : 0);

          // reserve space for the overflow indicator
          const needsOverflowIndicator = i < chips.length - 1;
          const spaceNeededForOverflow = needsOverflowIndicator ? gap + overflowChipWidth : 0;

          if (totalWidth + widthWithGap + spaceNeededForOverflow > availableWidth) {
            break;
          }

          totalWidth += widthWithGap;
          count++;
        }

        // only show overflow if we're hiding chips
        setVisibleChipCount(count < selectedOptions.length ? count : null);
      });
    };
    calculateVisibleChips();

    // handle resizing
    const resizeObserver = new ResizeObserver(calculateVisibleChips);
    resizeObserver.observe(selectContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
    // Depend on a stable string key for the selection rather than the array's
    // identity — callers commonly pass a fresh `selectedValues` array literal on
    // every render, which would otherwise re-run this effect (and its
    // setVisibleChipCount) each render. During a burst of re-renders React skips
    // its same-value setState bailout, turning that into an infinite update loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues?.join(" "), isMultiSelect, selectedOptions.length]);
};
